import {
  $createTextNode,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  LexicalEditor,
  NodeKey,
} from 'lexical';
import { uploadImage } from '@/components/file-thumbnail/utils';
import UploadImageNode, {
  $createUploadImageNode,
} from '@/components/LexicalEditor/nodes/UploadImageNode';
import { validateImageSize } from './validateImageSize';

type ToastLike = {
  show: (opts: {
    severity: 'error' | 'info' | 'success' | 'warning';
    message: string;
    duration?: number;
  }) => void;
};

/**
 * 파일 1개를 인라인 이미지로 삽입 (로딩 placeholder → 업로드 → ImageNode 교체).
 * 공용 DragDropPastePlugin 을 건드리지 않기 위한 별도 헬퍼 — 게시판/소셜 영향 방지.
 */
export async function insertInlineImageFromFile(
  editor: LexicalEditor,
  file: File,
  toast: ToastLike,
  t: (s: string) => string,
): Promise<void> {
  const validation = await validateImageSize(file);
  if (!validation.valid) {
    toast.show({
      severity: 'error',
      message: t('이미지는 최대 3000px*6000px까지 첨부 가능합니다.'),
    });
    return;
  }

  if (file.size >= 100000000) {
    toast.show({
      severity: 'info',
      message: t(
        '용량이 큰 이미지는 불러오는 데 시간이 걸릴 수 있어요. 잠시만 기다려 주세요.',
      ),
      duration: 5000,
    });
  }

  let nodeKey: NodeKey | undefined;
  const objectUrl = URL.createObjectURL(file);

  editor.update(() => {
    // 0. 선택 영역 가드 — 없으면 루트 끝으로
    const sel = $getSelection();
    if (!$isRangeSelection(sel)) {
      $getRoot().selectEnd();
    }

    // 1. 업로드 중 로딩 placeholder 노드 삽입 (inline)
    const node = $createUploadImageNode({
      src: objectUrl,
      altText: file.name,
      status: 'loading',
      inline: true,
    });
    $insertNodes([node]);
    nodeKey = node.getKey();

    // 2. 줄바꿈 없이 이미지 뒤 빈 텍스트로 커서 이동 — 같은 줄 타이핑
    const cursor = $createTextNode('');
    node.insertAfter(cursor);
    cursor.select();
  });

  try {
    const uploaded = await uploadImage(file);
    const serverUrl = uploaded.url;

    // 3. 서버 URL 로딩 완료 후 실제 이미지로 교체
    await new Promise<void>((resolve) => {
      const img = new Image();
      img.onload = () => {
        editor.update(() => {
          const node = $getNodeByKey(nodeKey!) as UploadImageNode | null;
          if (node) {
            node.setSrc(serverUrl);
            node.setStatus('success');
          }
        });
        resolve();
      };
      img.onerror = () => {
        editor.update(() => {
          const node = $getNodeByKey(nodeKey!) as UploadImageNode | null;
          node?.setStatus('error');
        });
        resolve();
      };
      img.src = serverUrl;
    });
  } catch {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey!) as UploadImageNode | null;
      node?.setStatus('error');
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
